import React, {useContext, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import BedRockContext from "../../BedRockContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {GenericTable} from "../generic/table/GenericTable";
import {defaultList} from "../generic/InitialStates";
import {HeaderColumnFormatter} from "../../column-formatters/HeaderColumnFormatter";
import {ActionsColumnFormatter} from "../../column-formatters/ActionsColumnFormatter";
import {ENTITY_ID_RAW_PURCHASE} from "../generic/Constants";
import {formatDateTime} from "../../utils/Utils";
import {buildReviewDetails} from "./qrReviewDetails";
import {QrDraftWhyDialog} from "./QrDraftWhyDialog";
import {confirmAndDiscardQrDraft, QR_DRAFT_FILTERS} from "./qrDraftActions";

// Plain components (no hooks) so they are safe inside column formatters.
function DraftVehicleCell({row, onOpen}) {
    return (
        <>
            <a href="#" onClick={(e) => { e.preventDefault(); onOpen(row); }}>
                {HeaderColumnFormatter(row.vehicleNumber || "N/A", row)}
            </a>
            <span className="badge badge-light-info ms-2" title="Created automatically from a QR scan">
                <i className="bi bi-qr-code me-1"></i>QR
            </span>
        </>
    );
}

function recordedAt(value) {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d)) return "";
    const sameDay = d.toDateString() === new Date().toDateString();
    return sameDay
        ? d.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit", hour12: false})
        : formatDateTime(value);
}

// Needs review: first reason (label + detail) inline, the full explanation behind "Why?".
function DraftStatusCell({row, onWhy}) {
    if (row.needsReview === true) {
        const details = buildReviewDetails(row.reviewReasons, row.reviewNote);
        const first = details[0];
        return (
            <div style={{maxWidth: 360}}>
                <span className="badge badge-light-warning">Needs review</span>
                {first && (
                    <div className="mt-1">
                        <span className="fw-bold">{first.label}</span>
                        {first.detail && <div className="text-muted fs-8 text-truncate" title={first.detail}>{first.detail}</div>}
                    </div>
                )}
                <a href="#" className="fs-8 fw-semibold" onClick={(e) => { e.preventDefault(); onWhy(row); }}>
                    {details.length > 1 ? `Why? (+${details.length - 1} more)` : "Why? What to do"}
                </a>
            </div>
        );
    }
    const at = recordedAt(row.purchaseFirstStepDate || row.createdOn);
    return (
        <div style={{maxWidth: 360}}>
            <span className="badge badge-light-primary">Waiting for empty weighing</span>
            <div className="text-muted fs-8 mt-1">
                Loaded weight {formatTons(row.loadedWeight)}{at ? ` recorded at ${at}` : ""}.
                Revcon will complete it automatically when the truck comes back empty.
            </div>
        </div>
    );
}

function formatTons(value) {
    if (value === null || value === undefined || value === "") return "-";
    const n = parseFloat(value);
    return isNaN(n) ? "-" : `${n.toFixed(3)} t`;
}

export function DraftPurchasesPage() {
    const myData = useContext(BedRockContext);
    const navigate = useNavigate();
    const [reload, setReload] = useState(1);
    const [whyRow, setWhyRow] = useState(null);

    const profileBreadCrumbs = [
        {title: "Home", path: "/dashboard", isSeparator: false, isActive: false},
        {title: "", path: "", isSeparator: true, isActive: false},
        {title: "Purchases", path: "/purchases-list", isSeparator: false, isActive: false},
        {title: "", path: "", isSeparator: true, isActive: false},
    ];

    function editDraft(row) {
        navigate(`/purchase-new/edit/${row.id}`);
    }

    async function discardDraft(row) {
        const discarded = await confirmAndDiscardQrDraft(row.id);
        if (discarded) setReload(prev => prev + 1);
    }

    const columns = useMemo(() => [
        {
            dataField: "purchaseFirstStepDate",
            text: "Date / Time",
            formatter: (cell, row) => formatDateTime(cell || row.createdOn),
            headerStyle: {width: "170px"}
        },
        {
            dataField: "vehicleNumber",
            text: "Vehicle #",
            search: true,
            formatter: (cell, row) => <DraftVehicleCell row={row} onOpen={editDraft}/>,
            headerStyle: {width: "170px"}
        },
        {
            dataField: "supplier.name",
            text: "Supplier",
            formatter: (cell, row) => row.supplier?.name || "-"
        },
        {
            dataField: "itemsList",
            text: "Product",
            formatter: (cell) => (Array.isArray(cell) && cell[0]?.inventoryItemName) || "-"
        },
        {
            dataField: "loadedWeight",
            text: "Loaded Weight",
            formatter: (cell) => formatTons(cell),
            headerStyle: {width: "130px"}
        },
        {
            dataField: "needsReview",
            text: "Status",
            formatter: (cell, row) => <DraftStatusCell row={row} onWhy={setWhyRow}/>
        },
        {
            dataField: "action",
            text: "Actions",
            headerStyle: {width: "190px"},
            formatter: ActionsColumnFormatter,
            formatExtraData: {
                buttons: [
                    {name: "Edit Draft", label: "Edit", color: "primary", method: editDraft},
                    {name: "Discard Draft", label: "Discard", color: "danger", method: discardDraft},
                ],
            },
            classes: "text-right pr-0",
            headerClasses: "text-right pr-3",
        },
    ], []);

    useEffect(() => {
        if (myData && myData.setBaseData) {
            myData.setBaseData({toolBarButtons: []});
        }
    }, []);

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Draft Purchases</PageTitle>
            <div className="card card-body">
                <div className="alert alert-primary d-flex align-items-center mb-4" role="alert">
                    <i className="bi bi-qr-code-scan me-3 fs-4"></i>
                    <div>
                        <strong>Draft Purchases</strong> — created automatically by the QR scanner.
                        <br/>
                        <small>
                            Drafts are kept on this device only and are sent to the server once they are completed.
                            Use Edit to check and finish a draft, or Discard to remove it.
                        </small>
                    </div>
                </div>
                <GenericTable
                    reload={reload}
                    defaultData={defaultList}
                    columns={columns}
                    entityId={ENTITY_ID_RAW_PURCHASE}
                    fixedFilterColumn="isPartialRecord"
                    fixedFilterData={true}
                    extraFilters={QR_DRAFT_FILTERS}
                    sortBy="createdOn"
                    sortOrder="desc"
                />
            </div>
            <QrDraftWhyDialog row={whyRow} onClose={() => setWhyRow(null)} onEdit={editDraft}/>
        </>
    );
}
